import { useCallback, useEffect, useRef, useState } from 'react'
import { FlipHorizontal, X } from 'lucide-react'
import type { Concept } from '@/types/Concept.types.ts'
import { ConceptCanvas, extractAssignments } from '../canvas/ConceptCanvas'
import type { Drawing } from '@/types'
import type { Player } from '@/types'
import { ConceptToolbar } from './ConceptToolbar'
import { TargetingTooltip } from './TargetingTooltip'
import { ConceptTypeTooltip } from './ConceptTypeTooltip'
import { CanvasViewportProvider } from '../../contexts/CanvasViewportContext'
import { generateThumbnail } from '@/utils/Thumbnail.ts'
import { ColorPickerDialog } from '../toolbar/dialogs/ColorPickerDialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'
import { useDialogAutoClose } from '@/hooks'
import { ModifierSelector } from '../formation-editor/ModifierSelector'
import type { FormationState } from '@/types/FormationState.types'
import { DEFAULT_FORMATION_STATE } from '@/types/FormationState.types'
import { modifierResolutionService } from '@/services/ModifierResolution.service'
import { useConceptForm } from '@/hooks/UseConceptForm'
import { useCanvasDrawing } from '@/hooks/UseCanvasDrawing'
import './concept-dialog.css'

interface ConceptDialogProps {
	isOpen: boolean
	onClose: () => void
	mode: 'create' | 'edit' | 'save-as'
	concept?: Concept
	teamId: string
	playbookId?: string
	onSave: (_concept: Partial<Concept>) => Promise<void>
}

export function ConceptDialog({
								  isOpen,
								  onClose,
								  mode,
								  concept,
								  teamId,
								  playbookId,
								  onSave,
							  }: ConceptDialogProps) {
	// Use extracted hooks for form and canvas state
	const conceptForm = useConceptForm({ concept, mode })
	const canvasDrawing = useCanvasDrawing()

	const [isSaving, setIsSaving] = useState(false)
	const [flipCanvas, setFlipCanvas] = useState<(() => void) | null>(null)
	const [selectedModifiers, setSelectedModifiers] = useState<string[]>([])
	const [computedState, setComputedState] = useState<FormationState | null>(null)

	const canvasContainerRef = useRef<HTMLDivElement>(null)
	const getCanvasStateRef = useRef<(() => {
		drawings: Drawing[]
		players: Player[]
		targetingMode: any
	}) | null>(null)

	// Memoized flip callback
	const handleFlipReady = useCallback((fn: () => void) => {
		setFlipCanvas(() => fn)
	}, [])

	// Store canvas state getter
	const handleCanvasStateReady = useCallback((getState: () => {
		drawings: Drawing[]
		players: Player[]
		targetingMode: any
	}) => {
		getCanvasStateRef.current = getState
	}, [])

	// Auto-close color picker when cursor moves away
	useDialogAutoClose({
		isOpen: canvasDrawing.state.showColorPicker,
		onClose: () => canvasDrawing.setShowColorPicker(false),
		dataAttribute: 'data-color-dialog',
	})

	// Compute formation state when modifiers change
	// Note: Using empty modifiers/conflicts for preview - actual computation happens on save
	useEffect(() => {
		if (conceptForm.form.category === 'blocking' && selectedModifiers.length > 0) {
			const baseState = DEFAULT_FORMATION_STATE
			const result = modifierResolutionService.computeState(
				baseState,
				selectedModifiers,
				new Map(),
				[],
			)
			setComputedState(result.state)
		} else {
			setComputedState(null)
		}
	}, [conceptForm.form.category, selectedModifiers])

	// Reset modifiers when category changes away from blocking
	useEffect(() => {
		if (conceptForm.form.category !== 'blocking') {
			setSelectedModifiers([])
			setComputedState(null)
		}
	}, [conceptForm.form.category])

	if (!isOpen) return null

	async function handleSave() {
		// Validate before save
		if (!conceptForm.isValid) {
			conceptForm.setTouched(true)
			return
		}

		setIsSaving(true)
		try {
			// Generate thumbnail from canvas
			let thumbnail: string | null = null
			if (canvasContainerRef.current) {
				try {
					thumbnail = await generateThumbnail(canvasContainerRef.current)
				} catch (error) {
					console.warn('Failed to generate thumbnail:', error)
				}
			}

			// Extract assignments from canvas state
			const assignments = getCanvasStateRef.current
				? extractAssignments(
					getCanvasStateRef.current().drawings,
					getCanvasStateRef.current().players,
					conceptForm.form.targetingMode,
				)
				: []

			const conceptData: Partial<Concept> = {
				name: conceptForm.form.name.trim(),
				description: conceptForm.form.description.trim() || null,
				thumbnail,
				targeting_mode: conceptForm.form.targetingMode,
				ball_position: conceptForm.form.ballPosition,
				play_direction: conceptForm.form.playDirection,
				category: conceptForm.form.category,
				playbook_id: playbookId ? playbookId : null,
				assignments: assignments as any,
			}

			await onSave(conceptData)
			onClose()
		} catch (error) {
			console.error('Failed to save concept:', error)
		} finally {
			setIsSaving(false)
		}
	}

	function handleFlip() {
		if (flipCanvas) {
			flipCanvas()
		}
	}

	return (
		<div className="concept-dialog-overlay">
			<div className="concept-dialog">
				<Button
					variant="ghost"
					size="icon"
					onClick={onClose}
					aria-label="Close dialog"
					className="concept-dialog-close-button"
				>
					<X className="concept-dialog-close-icon" />
				</Button>

				{/* Name */}
				<div className="concept-dialog-header">
					<label className="concept-dialog-label">Concept Name</label>
					<Input
						type="text"
						value={conceptForm.form.name}
						onChange={(e) => conceptForm.updateField('name', e.target.value)}
						placeholder="e.g., Mesh, Spacing, Y-Cross"
						maxLength={100}
						className={conceptForm.errors.name && conceptForm.touched ? 'border-destructive' : ''}
						aria-invalid={!!(conceptForm.errors.name && conceptForm.touched)}
					/>
					{conceptForm.errors.name && conceptForm.touched && (
						<p className="concept-dialog-error">{conceptForm.errors.name}</p>
					)}
				</div>

				{/* Canvas Area */}
				<div className="concept-dialog-canvas-area">
					{/* Left Toolbar */}
					<ConceptToolbar
						selectedTool={canvasDrawing.state.tool}
						onToolChange={canvasDrawing.setTool}
						color={canvasDrawing.state.color}
						onColorChange={canvasDrawing.setColor}
						hashAlignment={canvasDrawing.state.hashAlignment}
						onHashAlignmentChange={canvasDrawing.setHashAlignment}
						showColorPicker={canvasDrawing.state.showColorPicker}
						onShowColorPickerChange={canvasDrawing.setShowColorPicker}
						showDrawOptions={canvasDrawing.state.showDrawOptions}
						onShowDrawOptionsChange={canvasDrawing.setShowDrawOptions}
						lineStyle={canvasDrawing.state.lineStyle}
						lineEnd={canvasDrawing.state.lineEnd}
						brushSize={canvasDrawing.state.brushSize}
						pathMode={canvasDrawing.state.pathMode}
						onLineStyleChange={canvasDrawing.setLineStyle}
						onLineEndChange={canvasDrawing.setLineEnd}
						onBrushSizeChange={canvasDrawing.setBrushSize}
						onPathModeChange={canvasDrawing.setPathMode}
					/>

					{/* Canvas */}
					<div ref={canvasContainerRef} className="concept-dialog-canvas-container">
						<CanvasViewportProvider>
							<ConceptCanvas
								drawingState={{
									tool: canvasDrawing.state.tool,
									color: canvasDrawing.state.color,
									brushSize: canvasDrawing.state.brushSize,
									lineStyle: canvasDrawing.state.lineStyle,
									lineEnd: canvasDrawing.state.lineEnd,
									pathMode: canvasDrawing.state.pathMode,
									eraseSize: 40,
									snapThreshold: 20,
								}}
								hashAlignment={canvasDrawing.state.hashAlignment}
								targetingMode={conceptForm.form.targetingMode}
								initialAssignments={concept?.assignments as any}
								onCanvasStateReady={handleCanvasStateReady}
								onFlipReady={handleFlipReady}
							/>
						</CanvasViewportProvider>
					</div>

					{/* Color Picker Dialog */}
					{canvasDrawing.state.showColorPicker && (
						<div className="concept-dialog-color-picker">
							<ColorPickerDialog
								currentColor={canvasDrawing.state.color}
								onColorChange={canvasDrawing.setColor}
								onClose={() => canvasDrawing.setShowColorPicker(false)}
								useRelativePosition={true}
							/>
						</div>
					)}
				</div>

				{/* Bottom Controls */}
				<div className="concept-dialog-footer">
					<div className="concept-dialog-footer-controls">
						{/* Targeting Mode */}
						<div className="concept-dialog-control-group">
							<label className="concept-dialog-control-label">Target Type:</label>
							<Select
								value={conceptForm.form.targetingMode}
								onValueChange={(v) => conceptForm.updateField('targetingMode', v as any)}
							>
								<SelectTrigger className="concept-dialog-select-trigger">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="absolute_role">Player Based</SelectItem>
									<SelectItem value="relative_selector">Alignment Based</SelectItem>
								</SelectContent>
							</Select>
							<TargetingTooltip />
						</div>

						{/* Play Direction */}
						<div className="concept-dialog-control-group">
							<label className="concept-dialog-control-label">Direction:</label>
							<Select
								value={conceptForm.form.playDirection}
								onValueChange={(v) => conceptForm.updateField('playDirection', v as any)}
							>
								<SelectTrigger className="concept-dialog-select-trigger-direction">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="na">N/A</SelectItem>
									<SelectItem value="left">Left</SelectItem>
									<SelectItem value="right">Right</SelectItem>
								</SelectContent>
							</Select>
						</div>

						{/* Flip Button */}
						<button onClick={handleFlip} className="concept-dialog-flip-button">
							<FlipHorizontal className="concept-dialog-flip-icon" />
							Flip
						</button>

						{/* Concept Category */}
						<div className="concept-dialog-control-group">
							<label className="concept-dialog-control-label">Category:</label>
							<Select
								value={conceptForm.form.category}
								onValueChange={(v) => conceptForm.updateField('category', v as any)}
							>
								<SelectTrigger className="concept-dialog-select-trigger">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="route">Route</SelectItem>
									<SelectItem value="blocking">Blocking</SelectItem>
									<SelectItem value="motion">Motion</SelectItem>
									<SelectItem value="protection">Protection</SelectItem>
								</SelectContent>
							</Select>
							<ConceptTypeTooltip />
						</div>
					</div>

					{/* Modifier Selection */}
					{conceptForm.form.category === 'blocking' && playbookId && (
						<div className="concept-dialog-modifier-section">
							<h3 className="concept-dialog-modifier-title">
								Formation Modifiers
							</h3>
							<ModifierSelector
								playbookId={playbookId}
								selectedModifiers={selectedModifiers}
								onModifiersChange={setSelectedModifiers}
							/>

							{/* State Changes Preview */}
							{computedState && (
								<div className="concept-dialog-state-preview">
									<h4 className="concept-dialog-state-title">
										State Changes:
									</h4>
									<div className="concept-dialog-state-list">
										{Object.entries(computedState).map(([key, value]) => {
											if (DEFAULT_FORMATION_STATE[key as keyof FormationState] === value) {
												return null
											}
											return (
												<div key={key} className="concept-dialog-state-item">
													<span className="concept-dialog-state-key">{key}:</span>
													<span className="concept-dialog-state-value">{String(value)}</span>
												</div>
											)
										})}
									</div>
								</div>
							)}
						</div>
					)}

					{/* Action Buttons */}
					<div className="concept-dialog-action-buttons">
						<button onClick={onClose} className="concept-dialog-cancel-button">
							Cancel
						</button>
						<button
							onClick={handleSave}
							disabled={!conceptForm.isValid || isSaving}
							className="concept-dialog-save-button"
						>
							{isSaving ? 'Saving...' : mode === 'edit' ? 'Update' : 'Create'}
						</button>
					</div>
				</div>
			</div>
		</div>
	)
}
